import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface FreeModeContextType {
  freeMode: boolean;
  loading: boolean;
  ready: boolean; // New: indicates if initial load is complete
  refetch: () => Promise<void>;
}

const FreeModeContext = createContext<FreeModeContextType>({
  freeMode: false,
  loading: true,
  ready: false,
  refetch: async () => {},
});

export const FreeModeProvider = ({ children }: { children: ReactNode }) => {
  const [freeMode, setFreeMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);

  const fetchFreeMode = async () => {
    try {
      const { data } = await supabase
        .from('platform_settings')
        .select('value')
        .eq('key', 'free_mode')
        .single();
      
      if (data) {
        setFreeMode((data.value as any)?.enabled ?? false);
      }
    } catch (error) {
      console.error('Error fetching free mode:', error);
    } finally {
      setLoading(false);
      setReady(true);
    }
  };

  useEffect(() => {
    fetchFreeMode();
    
    // Subscribe to changes
    const channel = supabase
      .channel('platform_settings_changes')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'platform_settings',
          filter: 'key=eq.free_mode'
        },
        (payload) => {
          if (payload.new) {
            setFreeMode((payload.new as any).value?.enabled ?? false);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // IMPORTANT: Always render children immediately to allow React Router to read
  // the current URL on page load. Not doing so breaks deep linking and refresh.
  // The freeMode value will simply be false until the setting loads.

  return (
    <FreeModeContext.Provider value={{ freeMode, loading, ready, refetch: fetchFreeMode }}>
      {children}
    </FreeModeContext.Provider>
  );
};

export const useFreeMode = () => useContext(FreeModeContext);
